import { ref, readonly } from 'vue'
import { useRouter, useRoute } from '#vue-router'
import type { RegisterData, AuthResponse, LoginData } from '~/types/auth'
import type { User } from '~/types/user'
import { useApiCall } from '~/composables/useApi'
import { useUser } from '~/composables/useUser'
import { type AnalyticsAuthMethod, useAnalytics } from '~/composables/useAnalytics'

// Social login creates the account on the first sign-in and the response has no flag
// for that, so an account created this recently counts as a sign-up
const NEW_ACCOUNT_MAX_AGE_MS = 2 * 60 * 1000

export const useAuth = () => {
  const {
    setUser,
    setToken,
    setLoading,
    clearUser,
    isLoading,
    isProfileIncomplete,
    initFavoriteIds,
    resetFavoriteIds,
    initLikeIds,
    resetLikeIds,
    getToken
  } = useUser()
  const router = useRouter()
  const route = useRoute()
  const error = ref<string | null>(null)
  const { getPathWithLocale } = useLocalizedPath()
  const { trackEvent } = useAnalytics()

  const trackSocialAuth = (user: User, method: AnalyticsAuthMethod) => {
    const accountAge = Date.now() - new Date(user.created_at).getTime()
    const isNewAccount = accountAge < NEW_ACCOUNT_MAX_AGE_MS
    const eventName = isNewAccount ? 'sign_up' : 'login'

    trackEvent(eventName, { method })
  }

  const handleAuth = async (response: AuthResponse) => {
    setUser(response.data.user)

    if (response.data.token) {
      setToken(response.data.token)
      await nextTick()
      resetFavoriteIds()
      await initFavoriteIds()
      resetLikeIds()
      await initLikeIds()
    }
  }

  const redirectAfterAuth = async () => {
    if (isProfileIncomplete.value) {
      await router.push(getPathWithLocale('/panel/profile/edit'))
    } else {
      if (typeof route.name === 'string' && route.name.startsWith('login')) {
        await router.push(getPathWithLocale('/'))
      }
    }
  }

  const handleAuthWithRedirect = async (response: AuthResponse) => {
    await handleAuth(response)
    await redirectAfterAuth()
  }

  const register = async (data: RegisterData): Promise<AuthResponse> => {
    setLoading(true)
    error.value = null

    try {
      const response = await useApiCall<AuthResponse>('/register', {
        method: 'POST',
        body: data
      })

      if (response.status === 'success') {
        trackEvent('sign_up', { method: 'phone' })
        await router.push(getPathWithLocale('/register/success'))
      }

      return response
    } catch (err: unknown) {
      const apiError = err as { data?: { message?: string; error?: string }; message?: string }

      let errorMessage = 'Ошибка регистрации'

      if (apiError.data?.message) {
        errorMessage = apiError.data.message
      } else if (apiError.data?.error) {
        errorMessage = apiError.data.error
      }

      error.value = errorMessage
      throw new Error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const login = async (data: LoginData, options?: { skipRedirect?: boolean }): Promise<AuthResponse> => {
    setLoading(true)
    error.value = null

    try {
      const response = await useApiCall<AuthResponse>('/login', {
        method: 'POST',
        body: data
      })

      if (response.status === 'success' && response.data) {
        trackEvent('login', { method: 'phone' })
        await handleAuth(response)
        if (!options?.skipRedirect && typeof route.name === 'string' && route.name.startsWith('login')) {
          await router.push(getPathWithLocale('/'))
        }
      }

      return response
    } catch (err: unknown) {
      const apiError = err as {
        data?: { message?: string; error?: string }
        message?: string
        status?: number
      }

      let errorMessage = 'Ошибка входа'

      if (apiError.data?.message) {
        errorMessage = apiError.data.message
      } else if (apiError.data?.error) {
        errorMessage = apiError.data.error
      } else if (apiError.status === 401) {
        errorMessage = 'Неверный email или пароль'
      }

      error.value = errorMessage
      throw new Error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const logout = async () => {
    if (!getToken()) return
    try {
      await useApiCall('/logout', { method: 'POST' })
    } catch (err) {
      console.error('Logout error:', err)
    } finally {
      clearUser()
      const policyStore = usePrivacyPolicyModalStore()
      policyStore.$reset()
      const forbiddenPaths = ['profile', 'favorites', 'b2b', 'panel']
      if (forbiddenPaths.some((p) => route.path.includes(p))) {
        router.push(getPathWithLocale('/'))
      }
      await initLikeIds()
    }
  }

  const logoutAndRedirectToLogin = async () => {
    await logout()
    await router.push(getPathWithLocale('/login'))
  }

  const { checkAndPromptPolicy } = usePrivacyPolicy()

  const handleAuthWithPolicyCheck = async (response: AuthResponse, onSuccess?: () => void) => {
    await handleAuth(response)
    await checkAndPromptPolicy(
      async () => {
        await redirectAfterAuth()
        onSuccess?.()
      },
      logout
    )
  }

  return {
    register,
    login,
    logout,
    logoutAndRedirectToLogin,
    handleAuth,
    handleAuthWithRedirect,
    handleAuthWithPolicyCheck,
    trackSocialAuth,
    redirectAfterAuth,
    isLoading,
    error: readonly(error)
  }
}
